'use client'

import { useContext, useEffect } from 'react'
import { Button, Cell, Dialog, Divider, Image, Navigation, Radio, Text, Icon } from '@vinted/web-ui'
import { X24, Home24, LocationPin24 } from '@vinted/monochrome-icons'

import { useTranslate } from '@marketplace-web/shared/i18n'
import { useTracking } from '@marketplace-web/shared/event-tracker'
import { List, ScrollableArea } from '@marketplace-web/shared/ui-helpers'
import { ContentLoader } from '@marketplace-web/shared/common-components/content-loader'
import { UiState } from '@marketplace-web/shared/ui-state'
import {
  GetShippingLabelContext,
  useConversationContext,
} from '@marketplace-web/domain/core-conversation'
import {
  GenerateLabelModal,
  DropOffsPointType,
  LabelOptionsDropOffsModel,
} from '@marketplace-web/domain/conversation-shipping-data'

import { clickEvent } from '_libs/common/event-tracker/events'
import { ClickableElement } from 'constants/tracking/clickable-elements'
import { Screen } from 'constants/tracking/screens'

type Props = {
  isOpen: boolean
}

const DropOffPointTypeSelectionDialog = ({ isOpen }: Props) => {
  const { track } = useTracking()
  const translate = useTranslate('conversation.drop_off_point_type_selection')
  const { conversation } = useConversationContext()

  const shipmentId = conversation?.transaction?.shipment?.id
  const transactionId = conversation?.transaction?.id

  const {
    state: {
      labelOptions: { fetchLabelOptions, labelOptions, labelOptionsUiState },
      dropOffPointTypeSelection: {
        selectedDropOffPointType,
        isDropOffPointTypeTypeSubmitEnabled,
        handleClearDropOffPointTypeSelection,
        selectDropOffPointType,
      },
      activeModal: { setActiveModal },
    },
  } = useContext(GetShippingLabelContext)

  useEffect(() => {
    if (shipmentId && isOpen) fetchLabelOptions({ shipmentId })
  }, [shipmentId, isOpen, fetchLabelOptions])

  function renderCarrier() {
    if (!labelOptions?.carrier) return null

    return (
      <Cell
        divider
        prefix={
          <Image
            src={labelOptions?.carrier.iconUrl}
            size={Image.Size.Large}
            styling={Image.Styling.Rounded}
          />
        }
        title={labelOptions?.carrier.name}
        body={translate('carrier_body')}
      />
    )
  }

  const setSelectedDropOffPointType = (type: DropOffsPointType) => () => {
    selectDropOffPointType(type)
  }

  const trackSubmitButtonClick = () => {
    const pointType = selectedDropOffPointType === DropOffsPointType.Home ? 'home' : 'shop'

    const targetDetails = {
      transaction_id: transactionId,
      dropoff_type_key: pointType,
    }

    track(
      clickEvent({
        target: ClickableElement.SelectDropoffType,
        screen: Screen.DropoffTypeSelection,
        targetDetails: JSON.stringify(targetDetails),
      }),
    )
  }

  const handleSubmit = () => {
    trackSubmitButtonClick()
    setActiveModal(GenerateLabelModal.GetShippingLabelModal)
  }

  const handleClose = () => {
    handleClearDropOffPointTypeSelection()
    setActiveModal(GenerateLabelModal.None)
  }

  const renderDropOffPointTypeSelectionMethod = (dropOffsPointType: LabelOptionsDropOffsModel) => {
    const isHomeDelivery = dropOffsPointType.type === DropOffsPointType.Home

    return (
      <Cell
        key={dropOffsPointType.type}
        type={Cell.Type.Navigating}
        prefix={
          <Icon name={isHomeDelivery ? Home24 : LocationPin24} color={Icon.Color.GreyscaleLevel2} />
        }
        testId={`${dropOffsPointType.type}-drop-off-type-point-selection`}
        title={
          isHomeDelivery ? translate('selection_title_home') : translate('selection_title_shop')
        }
        body={
          <Text
            as="span"
            html
            text={
              isHomeDelivery ? translate('selection_body_home') : translate('selection_body_shop')
            }
          />
        }
        onClick={setSelectedDropOffPointType(dropOffsPointType.type)}
        suffix={
          <Radio
            testId={`${dropOffsPointType.type}-drop-off-type-point-selection-radio`}
            name={`${dropOffsPointType.type}-drop-off-type-point-selection-radio`}
            checked={dropOffsPointType.type === selectedDropOffPointType}
            value={dropOffsPointType.type}
            onChange={() => undefined}
            aria={{
              'aria-labelledby': undefined,
            }}
          />
        }
      />
    )
  }

  const renderDropOffPointTypeSelection = () => (
    <>
      <Cell body={<Text as="h2" type={Text.Type.Heading} text={translate('title')} />} />
      <List>{labelOptions?.dropOffs?.map(renderDropOffPointTypeSelectionMethod)}</List>
      <Divider />
    </>
  )

  const renderSubmitAction = () => (
    <div className="u-margin-top-auto">
      <Divider />
      <Cell>
        <Button
          styling={Button.Styling.Filled}
          testId="drop-off-type-point-type-selection-submit"
          text={translate('actions.continue')}
          disabled={!isDropOffPointTypeTypeSubmitEnabled}
          onClick={handleSubmit}
        />
      </Cell>
    </div>
  )

  const renderModalContent = () => {
    if (labelOptionsUiState === UiState.Pending)
      return <ContentLoader testId="drop-off-point-type-selection-loader" />

    return (
      <>
        {renderCarrier()}
        {renderDropOffPointTypeSelection()}
        {renderSubmitAction()}
      </>
    )
  }

  return (
    <Dialog show={isOpen}>
      <div className="u-fill-width">
        <ScrollableArea>
          <Navigation
            body={translate('dialog_title')}
            right={
              <Button
                styling={Button.Styling.Flat}
                onClick={handleClose}
                iconName={X24}
                testId="drop-off-point-type-selection-close"
                inline
              />
            }
          />
          <Divider />
          <div className="label-type-selection-modal__body">{renderModalContent()}</div>
        </ScrollableArea>
      </div>
    </Dialog>
  )
}

export default DropOffPointTypeSelectionDialog
